import { useState } from "react";
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAIAssistant } from "@/hooks/useAIAssistant";

interface DealInsightsBadgeProps {
  deal: {
    id: string;
    title: string;
    amount: number | null;
    ai_win_probability: number | null;
    accounts?: { name: string } | null;
    pipeline_stages?: { name: string } | null;
    expected_close_date?: string | null;
  };
}

export function DealInsightsBadge({ deal }: DealInsightsBadgeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [insights, setInsights] = useState<string | null>(null);
  const { getDealInsights, isLoading } = useAIAssistant();

  const probability = deal.ai_win_probability || 0;

  const getStatusConfig = () => {
    if (probability >= 70) {
      return {
        icon: CheckCircle,
        label: "Strong",
        color: "text-success",
        bgColor: "bg-success/10",
        borderColor: "border-success/30",
      };
    } else if (probability >= 40) {
      return {
        icon: TrendingUp,
        label: "Good",
        color: "text-warning",
        bgColor: "bg-warning/10",
        borderColor: "border-warning/30",
      };
    } else if (probability > 0) {
      return {
        icon: AlertTriangle,
        label: "At Risk",
        color: "text-destructive",
        bgColor: "bg-destructive/10",
        borderColor: "border-destructive/30",
      };
    }
    return {
      icon: Sparkles,
      label: "Unscored",
      color: "text-muted-foreground",
      bgColor: "bg-muted",
      borderColor: "border-muted",
    };
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;

  const handleGetInsights = async () => {
    if (insights) {
      setIsExpanded(!isExpanded);
      return;
    }

    setIsExpanded(true);
    const result = await getDealInsights(
      deal,
      deal.pipeline_stages?.name || "Unknown",
      deal.accounts?.name || "Unknown"
    );
    if (result) {
      setInsights(result);
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleGetInsights();
        }}
        disabled={isLoading}
        className={cn(
          "w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg border transition-all",
          config.bgColor,
          config.borderColor,
          "hover:ring-2 hover:ring-primary/20"
        )}
      >
        <div className="flex items-center gap-2">
          {isLoading ? (
            <Loader2 className={cn("h-3.5 w-3.5 animate-spin", config.color)} />
          ) : (
            <StatusIcon className={cn("h-3.5 w-3.5", config.color)} />
          )}
          <span className={cn("text-xs font-medium", config.color)}>
            {probability}% • {config.label}
          </span>
        </div>
        {insights && (
          isExpanded ? (
            <ChevronUp className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          )
        )}
        {!insights && !isLoading && (
          <Sparkles className="h-3 w-3 text-primary" />
        )}
      </button>

      {isExpanded && (
        <div className="animate-in slide-in-from-top-2 duration-200">
          {isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground p-2 bg-muted/50 rounded-lg">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Generating insights...</span>
            </div>
          ) : insights ? (
            <div className="text-xs text-muted-foreground p-2 bg-muted/50 rounded-lg border border-border/50 leading-relaxed">
              {insights}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
